import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { api, errMsg, useFetch } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Page, Spinner, ErrorBox, EmptyBox, Badge, Pager, fmt } from "./ui";

const ROLE = { owner: "Responsable", co: "Co-responsable", nurse: "Infirmière" };

export default function Users() {
  const { user: me } = useApp();
  const [params] = useSearchParams();
  const focus = params.get("user");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const { data, error, reload } = useFetch(`/admin/users?q=${encodeURIComponent(q)}&page=${page}`);
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const toggle = (u) => window.confirm(`${u.active ? "Désactiver" : "Réactiver"} le compte de ${u.email} ?`) && act(() => api.put(`/admin/users/${u.id}`, { active: !u.active }), "Compte mis à jour");
  const reset = (u) => window.confirm(`Envoyer un lien de réinitialisation à ${u.email} ?`) && act(() => api.post(`/admin/users/${u.id}/reset-link`), "Lien envoyé");
  return (
    <Page title="Utilisateurs" subtitle="Les mots de passe ne sont jamais affichés : la réinitialisation passe par un lien envoyé par e-mail." testid="admin-users">
      <label className="relative mb-4 block"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input className="field !pl-9" placeholder="Nom ou e-mail" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="users-search" /></label>
      {error ? <ErrorBox error={error} /> : !data ? <Spinner /> : data.items.length === 0 ? <EmptyBox text="Aucun utilisateur." /> : (
        <>
          <div className="card divide-y divide-slate-100">
            {data.items.map((u) => (
              <div key={u.id} className={`flex flex-wrap items-center gap-3 px-4 py-3 text-sm ${focus === u.id ? "bg-teal-50 ring-2 ring-inset ring-teal-500" : ""}`} data-testid="admin-user-row">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{u.first_name} {u.last_name} {u.is_superadmin && <Badge tone="teal">Super admin</Badge>} {!u.active && <Badge tone="red">Désactivé</Badge>}</div>
                  <div className="text-xs text-slate-500">{u.email}{u.phone ? ` · ${u.phone}` : ""} · inscrit le {fmt(u.created_at)} · dernière connexion {fmt(u.last_login)}</div>
                  <div className="mt-1 flex flex-wrap gap-1">{u.teams.length === 0 ? <Badge>Sans équipe</Badge> : u.teams.map((t) => <Link key={t.id} to={`/admin/comptes/${t.id}`}><Badge tone={t.status === "active" ? "slate" : "red"}>{t.name} · {ROLE[t.role]}</Badge></Link>)}</div>
                </div>
                {u.id !== me.id && <button className="btn-ghost !min-h-[36px] !px-3 text-xs" onClick={() => toggle(u)} data-testid="user-toggle-active-btn">{u.active ? "Désactiver" : "Réactiver"}</button>}
                <button className="btn-ghost !min-h-[36px] !px-3 text-xs" onClick={() => reset(u)} data-testid="user-reset-btn">Lien mot de passe</button>
              </div>
            ))}
          </div>
          <Pager page={page} total={data.total} per={data.per_page} setPage={setPage} />
        </>
      )}
    </Page>
  );
}
